import React, { ChangeEvent, useCallback } from 'react'
import cx from 'classnames'

import FormFieldGroup from '../FormField'

import CheckboxContext from './CheckboxGroup-Context'
import { CheckboxGroupProps } from './interfaces'

import cm from './CheckboxGroup.module.css'

/**
 * The CheckboxGroup component serves as a wrapper for CheckboxV2 components, allowing users to select one or more items from a grouped list.
 *
 * The CheckboxGroup component automatically expands to 100% of the width of its parent element. By adjusting the width of the parent component, you can effectively control the width of the CheckboxGroup.
 *
 * [Figma](https://www.figma.com/file/Xf38PdCKeISWnBoh0pLFIIWf/%F0%9F%A7%B1-MC-Desktop-Components?type=design&node-id=27013-18722&mode=design&t=HPBpXnDIMhUJhkU5-0)
 */

export const CheckboxGroup = ({
  title,
  disabled = false,
  children,
  orientation = 'vertical',
  value: groupValues,
  onChange,
  name,
  ariaLabelledBy,
  ...props
}: CheckboxGroupProps) => {
  const handleChange = useCallback(
    (checkboxValue: string, event: ChangeEvent<HTMLInputElement>) => {
      if (disabled) {
        return
      }

      const wasChecked = groupValues.includes(checkboxValue)

      if (wasChecked) {
        onChange?.(
          groupValues.filter((val) => val !== checkboxValue),
          event,
        )
      } else {
        onChange?.([...groupValues, checkboxValue], event)
      }
    },
    [groupValues, onChange, disabled],
  )

  const contextValue = React.useMemo(
    () => ({
      values: groupValues,
      handleChange,
      disabled,
    }),
    [groupValues, handleChange, disabled],
  )

  const checkboxGroupClasses = cx(cm.checkboxGroup, {
    [cm.horizontal]: orientation === 'horizontal',
    [cm.vertical]: orientation === 'vertical',
  })

  return (
    <CheckboxContext.Provider value={contextValue}>
      <FormFieldGroup
        id={props.id}
        title={title}
        tabIndex={props.tabIndex}
        className={props.className}
      >
        <fieldset
          className={cx(cm.fieldSet)}
          role="group"
          name={name}
          form={props.form}
          aria-labelledby={ariaLabelledBy}
          aria-disabled={disabled}
          disabled={disabled}
          onBlur={props.onBlur}
          onFocus={props.onFocus}
        >
          <legend title={title} className={cx(cm.legend)} aria-hidden="true" />
          <div className={checkboxGroupClasses}>{children}</div>
        </fieldset>
      </FormFieldGroup>
    </CheckboxContext.Provider>
  )
}
